import React, { useLayoutEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { ButtonProps } from './Button';
import { Stack } from '../stack';
import {
  button_base_css,
  button_icon_size_css,
  floating_button_css,
  getButtonKind,
  getFloatingButtonCss,
} from './styles';
import { css } from '@emotion/react';
import { IconLoadingDonuts, IconX } from '../icons/generated';
import { ButtonIcon } from './ButtonIcon';

export interface FloatingButtonProps extends Omit<ButtonProps, 'fill' | 'kind' | 'size' | 'type'> {
  opened?: boolean;
}

export const FloatingButton = React.forwardRef<HTMLButtonElement, FloatingButtonProps>(
  ({ startIcon, opened, children, disabled, className, loading, ...props }, ref) => {
    const isDisabled = disabled || loading;
    const [button_width, setButtonWidth] = useState<number>();
    const content_ref = useRef<HTMLDivElement>(null);
    const icon_props = {
      size: button_icon_size_css['medium'],
      color: 'currentColor',
    };
    const start_icon_node = startIcon && (
      <ButtonIcon {...icon_props}>{loading ? <IconLoadingDonuts size={30} /> : startIcon}</ButtonIcon>
    );

    useLayoutEffect(() => {
      if (content_ref.current) {
        setButtonWidth(content_ref.current.offsetWidth + 32);
      }
    }, [children]);

    return (
      <button
        type='button'
        css={[
          button_base_css,
          getButtonKind('black'),
          floating_button_css,
          getFloatingButtonCss(Boolean(opened), button_width),
          css`
            cursor: ${isDisabled ? 'not-allowed' : 'pointer'};
          `,
        ]}
        className={classNames('pds-floating-button', className)}
        disabled={isDisabled}
        {...props}
        ref={ref}
      >
        <>
          <Stack
            direction='row'
            gap={4}
            align='center'
            ref={content_ref}
            css={[
              opened
                ? `
              pointer-events: none;
              opacity: 0;
              transition: opacity 0.15s;`
                : `
              opacity: 1;
              transition: opacity 0.15s 0.15s;
            `,
            ]}
          >
            {start_icon_node}
            {children}
          </Stack>
          <Stack
            direction='row'
            align='center'
            css={[
              `
                position: absolute;
              `,
              opened
                ? `
              opacity: 1;
              transition: opacity 0.15s 0.15s;`
                : `
              opacity: 0;
              transition: opacity 0.15s;
            `,
            ]}
          >
            <IconX size={20} color='currentColor' />
          </Stack>
        </>
      </button>
    );
  },
);
